import React, { useEffect, useState, useCallback } from "react";
import Header from "../../../components/ui/Header";
import Sidebar from "../../../components/ui/Sidebar";
import Icon from "../../../components/AppIcon";
import axiosInstance from "../../../utils/axiosInstance";

const API_BASE = import.meta.env.VITE_API_BASE_URL;

// ─── helpers ──────────────────────────────────────────────────────────────────
const fmt = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

const fmtNum = (n) => new Intl.NumberFormat("en-IN").format(n || 0);

const today      = () => new Date().toISOString().slice(0, 10);
const monthStart = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};

// ─── sub-components ───────────────────────────────────────────────────────────
const StatCard = ({ label, value, sub, icon, color = "blue", highlight = false }) => {
  const colors = {
    blue:   "bg-blue-50 text-blue-600 border-blue-100",
    green:  "bg-emerald-50 text-emerald-600 border-emerald-100",
    amber:  "bg-amber-50 text-amber-600 border-amber-100",
    red:    "bg-red-50 text-red-600 border-red-100",
    violet: "bg-violet-50 text-violet-600 border-violet-100",
    gray:   "bg-gray-50 text-gray-600 border-gray-100",
  };
  return (
    <div className={`rounded-xl border p-4 ${highlight ? "bg-blue-600 border-blue-600 text-white" : "bg-white border-gray-200"}`}>
      <div className="flex items-start justify-between mb-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${highlight ? "bg-blue-500" : colors[color]}`}>
          <Icon name={icon} size={18} className={highlight ? "text-white" : ""} />
        </div>
      </div>
      <p className={`text-2xl font-bold ${highlight ? "text-white" : "text-gray-900"}`}>{value}</p>
      <p className={`text-xs font-medium mt-0.5 ${highlight ? "text-blue-100" : "text-gray-500"}`}>{label}</p>
      {sub && <p className={`text-[10px] mt-1 ${highlight ? "text-blue-200" : "text-gray-400"}`}>{sub}</p>}
    </div>
  );
};

const SectionTable = ({ columns, rows, emptyMsg = "No data" }) => (
  <div className="overflow-x-auto rounded-lg border border-gray-200">
    <table className="w-full text-xs">
      <thead className="bg-gray-50 border-b border-gray-200">
        <tr>
          {columns.map((c) => (
            <th
              key={c.key}
              className={`px-4 py-2.5 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap ${
                c.align === "right" ? "text-right" : ""
              }`}
            >
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100 bg-white">
        {!rows.length ? (
          <tr>
            <td colSpan={columns.length} className="px-4 py-8 text-center text-gray-400 text-xs">
              {emptyMsg}
            </td>
          </tr>
        ) : (
          rows.map((row, i) => (
            <tr key={i} className="hover:bg-gray-50/60 transition-colors">
              {columns.map((c) => (
                <td
                  key={c.key}
                  className={`px-4 py-2.5 text-gray-700 whitespace-nowrap ${
                    c.align === "right" ? "text-right font-medium" : ""
                  }`}
                >
                  {c.render ? c.render(row[c.key], row) : (row[c.key] ?? "—")}
                </td>
              ))}
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

const Badge = ({ label, color }) => {
  const cls = {
    green:  "bg-emerald-100 text-emerald-700",
    red:    "bg-red-100 text-red-700",
    amber:  "bg-amber-100 text-amber-700",
    gray:   "bg-gray-100 text-gray-500",
    violet: "bg-violet-100 text-violet-700",
    blue:   "bg-blue-100 text-blue-700",
  }[color] || "bg-gray-100 text-gray-600";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-semibold ${cls}`}>
      {label}
    </span>
  );
};

const Loading = () => (
  <div className="flex flex-col items-center justify-center py-16 gap-3">
    <div className="w-8 h-8 border-2 border-gray-200 border-t-blue-600 rounded-full animate-spin" />
    <span className="text-xs text-gray-400">Loading report...</span>
  </div>
);

// ─── Commission badge — shows per-test commission inline ──────────────────────
const CommissionBadge = ({ value, billAmount }) => {
  if (!value || value === 0) return <span className="text-gray-400 text-[10px]">—</span>;
  return (
    <div className="flex flex-col items-end">
      <span className="font-medium text-emerald-700">{fmt(value)}</span>
      {billAmount > 0 && (
        <span className="text-[9px] text-gray-400">
          {Math.round((value / billAmount) * 100)}% of bill
        </span>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// PartnerSettlementView
// Full partner settlement tab: summary cards + expandable center → test breakdown
// ─────────────────────────────────────────────────────────────────────────────
function PartnerSettlementView({ data, fmt, fmtNum }) {
  const [expandedCenters, setExpandedCenters] = useState({});
  const [expandedTests,   setExpandedTests]   = useState({}); // "centerName|testName" → bool

  const toggleCenter = (name) =>
    setExpandedCenters((p) => ({ ...p, [name]: !p[name] }));

  const toggleTest = (key) =>
    setExpandedTests((p) => ({ ...p, [key]: !p[key] }));

  // ── Build center → test → orders tree from data.rows ──────────────────────
  const tree = React.useMemo(() => {
    const map = {};
    for (const row of (data.rows || [])) {
      const c = row.partnerName;
      const t = row.testName;
      if (!map[c]) map[c] = { name: c, orders: 0, bill: 0, commission: 0, partner: 0, tests: {} };
      map[c].orders++;
      map[c].bill       += row.billAmount       || 0;
      map[c].commission += row.novusCommission  || 0;
      map[c].partner    += row.partnerShare     || 0;

      if (!map[c].tests[t]) map[c].tests[t] = {
        name: t, type: row.testType, orders: 0, bill: 0, commission: 0, partner: 0, rows: [],
      };
      map[c].tests[t].orders++;
      map[c].tests[t].bill       += row.billAmount      || 0;
      map[c].tests[t].commission += row.novusCommission || 0;
      map[c].tests[t].partner    += row.partnerShare    || 0;
      map[c].tests[t].rows.push(row);
    }
    // sort centers by bill desc, tests by bill desc inside
    return Object.values(map)
      .sort((a, b) => b.bill - a.bill)
      .map((c) => ({
        ...c,
        tests: Object.values(c.tests).sort((a, b) => b.bill - a.bill),
      }));
  }, [data.rows]);

  const typeColor = (t) => t === "RADIOLOGY" ? "violet" : "blue";

  return (
    <div className="space-y-5">
      {/* ── Summary stat cards ─────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <StatCard icon="Building2"   label="Total Orders"     value={fmtNum(data.summary?.totalOrders)}          highlight />
        <StatCard icon="IndianRupee" label="Total Bill"       value={fmt(data.summary?.totalBillAmount)}         color="blue" />
        <StatCard icon="Handshake"   label="Partner Share"    value={fmt(data.summary?.totalPartnerShare)}       color="violet" />
        <StatCard icon="TrendingUp"  label="Novus Commission" value={fmt(data.summary?.totalNovusCommission)}    color="green" />
        <StatCard icon="CheckCircle" label="Paid"             value={fmtNum(data.summary?.paidCount)}            color="green" />
        <StatCard icon="Clock"       label="Pending"          value={fmtNum(data.summary?.pendingCount)}         color="amber" />
      </div>

      {/* ── Info banner ────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-2.5 bg-blue-50 border border-blue-100 rounded-xl px-4 py-2.5">
        <Icon name="Info" size={14} className="text-blue-500 flex-shrink-0" />
        <p className="text-xs text-blue-700">
          Commission is calculated <strong>per test</strong>. Expand a center to see each test's individual commission.
        </p>
      </div>

      {/* ── Expandable Center → Test → Order tree ──────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {/* Table header */}
        <div className="px-5 py-3.5 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
            <Icon name="Building" size={15} className="text-blue-500" />
            Center-wise Breakdown
          </h3>
          <span className="text-[10px] text-gray-400 bg-gray-100 px-2.5 py-1 rounded-full">
            Click a center to expand test-wise commissions
          </span>
        </div>

        {/* Column headers */}
        <div className="grid grid-cols-12 gap-2 px-5 py-2 bg-gray-50 border-b border-gray-200">
          <div className="col-span-4 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Center / Test</div>
          <div className="col-span-1 text-[10px] font-semibold text-gray-500 uppercase tracking-wide text-center">Orders</div>
          <div className="col-span-2 text-[10px] font-semibold text-gray-500 uppercase tracking-wide text-right">Bill Amount</div>
          <div className="col-span-3 text-[10px] font-semibold text-gray-500 uppercase tracking-wide text-right">Novus Commission</div>
          <div className="col-span-2 text-[10px] font-semibold text-gray-500 uppercase tracking-wide text-right">Partner Share</div>
        </div>

        {tree.length === 0 && (
          <div className="px-5 py-10 text-center text-xs text-gray-400">No partner orders for this period</div>
        )}

        {tree.map((center) => {
          const isCenterOpen = !!expandedCenters[center.name];
          const commPct = center.bill > 0 ? Math.round((center.commission / center.bill) * 100) : 0;

          return (
            <div key={center.name} className="border-b border-gray-100 last:border-b-0">
              {/* ── CENTER ROW ──────────────────────────────────────────────── */}
              <div
                onClick={() => toggleCenter(center.name)}
                className={`grid grid-cols-12 gap-2 px-5 py-3.5 cursor-pointer select-none transition-colors ${
                  isCenterOpen ? "bg-blue-50/60" : "hover:bg-gray-50"
                }`}
              >
                {/* Name */}
                <div className="col-span-4 flex items-center gap-2.5 min-w-0">
                  <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 transition-transform ${isCenterOpen ? "rotate-90" : ""}`}>
                    <Icon name="ChevronRight" size={14} className="text-gray-400" />
                  </div>
                  <div className="w-7 h-7 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name="Building2" size={13} className="text-blue-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-gray-900 truncate">{center.name}</p>
                    <p className="text-[10px] text-gray-400">{center.tests.length} test type{center.tests.length !== 1 ? "s" : ""}</p>
                  </div>
                </div>

                {/* Orders */}
                <div className="col-span-1 flex items-center justify-center">
                  <span className="text-xs font-medium text-gray-700">{fmtNum(center.orders)}</span>
                </div>

                {/* Bill */}
                <div className="col-span-2 flex items-center justify-end">
                  <span className="text-xs font-semibold text-gray-900">{fmt(center.bill)}</span>
                </div>

                {/* Commission */}
                <div className="col-span-3 flex flex-col items-end justify-center">
                  <span className="text-xs font-semibold text-emerald-700">{fmt(center.commission)}</span>
                  <div className="flex items-center gap-1.5 mt-0.5 w-full justify-end">
                    <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-1.5 bg-emerald-500 rounded-full"
                        style={{ width: `${Math.min(commPct, 100)}%` }}
                      />
                    </div>
                    <span className="text-[9px] text-gray-400">{commPct}%</span>
                  </div>
                </div>

                {/* Partner share */}
                <div className="col-span-2 flex items-center justify-end">
                  <span className="text-xs font-semibold text-violet-700">{fmt(center.partner)}</span>
                </div>
              </div>

              {/* ── TEST ROWS (expanded) ─────────────────────────────────────── */}
              {isCenterOpen && (
                <div className="bg-gray-50/50 border-t border-gray-100">
                  {center.tests.map((test) => {
                    const testKey    = `${center.name}|${test.name}`;
                    const isTestOpen = !!expandedTests[testKey];
                    const tPct = test.bill > 0 ? Math.round((test.commission / test.bill) * 100) : 0;

                    return (
                      <div key={test.name} className="border-b border-gray-100 last:border-b-0">
                        {/* Test summary row */}
                        <div
                          onClick={() => toggleTest(testKey)}
                          className={`grid grid-cols-12 gap-2 pl-14 pr-5 py-2.5 cursor-pointer select-none transition-colors ${
                            isTestOpen ? "bg-violet-50/40" : "hover:bg-white"
                          }`}
                        >
                          {/* Name */}
                          <div className="col-span-4 flex items-center gap-2 min-w-0">
                            <div className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 transition-transform ${isTestOpen ? "rotate-90" : ""}`}>
                              <Icon name="ChevronRight" size={11} className="text-gray-400" />
                            </div>
                            <div className="w-6 h-6 bg-violet-100 rounded flex items-center justify-center flex-shrink-0">
                              <Icon name="FlaskConical" size={11} className="text-violet-600" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-xs font-medium text-gray-800 truncate">{test.name}</p>
                              <Badge label={test.type} color={typeColor(test.type)} />
                            </div>
                          </div>

                          {/* Orders */}
                          <div className="col-span-1 flex items-center justify-center">
                            <span className="text-xs text-gray-600">{fmtNum(test.orders)}</span>
                          </div>

                          {/* Bill */}
                          <div className="col-span-2 flex items-center justify-end">
                            <span className="text-xs text-gray-700">{fmt(test.bill)}</span>
                          </div>

                          {/* Commission */}
                          <div className="col-span-3 flex flex-col items-end justify-center">
                            <span className={`text-xs font-medium ${test.commission > 0 ? "text-emerald-700" : "text-gray-400"}`}>
                              {test.commission > 0 ? fmt(test.commission) : "—"}
                            </span>
                            {test.commission > 0 && (
                              <span className="text-[9px] text-gray-400">{tPct}% per order</span>
                            )}
                          </div>

                          {/* Partner share */}
                          <div className="col-span-2 flex items-center justify-end">
                            <span className="text-xs text-violet-700">{fmt(test.partner)}</span>
                          </div>
                        </div>

                        {/* ── ORDER ROWS (expanded inside test) ──────────────── */}
                        {isTestOpen && (
                          <div className="bg-white border-t border-gray-100">
                            {/* Sub-header */}
                            <div className="grid grid-cols-12 gap-2 pl-24 pr-5 py-1.5 bg-gray-50 border-b border-gray-100">
                              <div className="col-span-2 text-[9px] font-semibold text-gray-400 uppercase">Order</div>
                              <div className="col-span-2 text-[9px] font-semibold text-gray-400 uppercase">Date</div>
                              <div className="col-span-2 text-[9px] font-semibold text-gray-400 uppercase">Patient</div>
                              <div className="col-span-2 text-[9px] font-semibold text-gray-400 uppercase text-right">Bill</div>
                              <div className="col-span-2 text-[9px] font-semibold text-gray-400 uppercase text-right">Commission</div>
                              <div className="col-span-2 text-[9px] font-semibold text-gray-400 uppercase text-right">Settlement</div>
                            </div>

                            {test.rows.map((row, i) => (
                              <div
                                key={i}
                                className="grid grid-cols-12 gap-2 pl-24 pr-5 py-2 border-b border-gray-50 last:border-b-0 hover:bg-gray-50/60 transition-colors"
                              >
                                <div className="col-span-2 text-[10px] text-gray-600 font-mono">ORD-{row.orderId}</div>
                                <div className="col-span-2 text-[10px] text-gray-500">
                                  {new Date(row.date).toLocaleDateString("en-IN")}
                                </div>
                                <div className="col-span-2 text-[10px] text-gray-700 truncate">{row.patientName}</div>
                                <div className="col-span-2 text-[10px] text-gray-700 text-right">{fmt(row.billAmount)}</div>
                                <div className="col-span-2 text-right">
                                  {row.novusCommission > 0 ? (
                                    <div className="flex flex-col items-end">
                                      <span className="text-[10px] font-medium text-emerald-700">{fmt(row.novusCommission)}</span>
                                      <span className="text-[9px] text-gray-400">
                                        {row.billAmount > 0 ? Math.round((row.novusCommission / row.billAmount) * 100) : 0}%
                                      </span>
                                    </div>
                                  ) : (
                                    <span className="text-[10px] text-gray-400">—</span>
                                  )}
                                </div>
                                <div className="col-span-2 flex justify-end items-center">
                                  <Badge
                                    label={row.settlementStatus}
                                    color={row.settlementStatus === "Paid" ? "green" : "amber"}
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Center totals footer */}
                  <div className="grid grid-cols-12 gap-2 pl-14 pr-5 py-2.5 bg-blue-50/60 border-t border-blue-100">
                    <div className="col-span-4 text-[10px] font-semibold text-blue-700">
                      Total — {center.name}
                    </div>
                    <div className="col-span-1 text-center text-[10px] font-semibold text-blue-700">{fmtNum(center.orders)}</div>
                    <div className="col-span-2 text-right text-[10px] font-semibold text-blue-700">{fmt(center.bill)}</div>
                    <div className="col-span-3 text-right text-[10px] font-semibold text-emerald-700">{fmt(center.commission)}</div>
                    <div className="col-span-2 text-right text-[10px] font-semibold text-violet-700">{fmt(center.partner)}</div>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* Grand totals footer */}
        {tree.length > 0 && (
          <div className="grid grid-cols-12 gap-2 px-5 py-3.5 bg-gray-900 rounded-b-xl">
            <div className="col-span-4 text-xs font-bold text-white">Grand Total</div>
            <div className="col-span-1 text-center text-xs font-bold text-white">
              {fmtNum(data.summary?.totalOrders)}
            </div>
            <div className="col-span-2 text-right text-xs font-bold text-white">
              {fmt(data.summary?.totalBillAmount)}
            </div>
            <div className="col-span-3 text-right text-xs font-bold text-emerald-400">
              {fmt(data.summary?.totalNovusCommission)}
            </div>
            <div className="col-span-2 text-right text-xs font-bold text-violet-300">
              {fmt(data.summary?.totalPartnerShare)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── TABS config ──────────────────────────────────────────────────────────────
const TABS = [
  { id: "collection",         label: "Collection",         icon: "Wallet",     color: "blue"   },
  { id: "revenue",            label: "Revenue",            icon: "TrendingUp", color: "green"  },
  { id: "partner-settlement", label: "Partner Settlement", icon: "Handshake",  color: "violet" },
  { id: "gateway",            label: "Payment Gateway",    icon: "CreditCard", color: "amber"  },
  { id: "walkin",             label: "Walk-In Billing",    icon: "Store",      color: "orange" },
];

// ─────────────────────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────────────────────
export default function ReportsPage() {
  const [sidebarOpen,       setSidebarOpen]       = useState(false);
  const [sidebarCollapsed,  setSidebarCollapsed]  = useState(false);
  const [activeTab,         setActiveTab]         = useState("collection");
  const [fromDate,          setFromDate]          = useState(today());
  const [toDate,            setToDate]            = useState(today());
  const [data,              setData]              = useState(null);
  const [loading,           setLoading]           = useState(false);
  const [error,             setError]             = useState(null);
  const [exporting,         setExporting]         = useState(false);

  // tab-specific extra filters
  const [partnerFilter,    setPartnerFilter]    = useState("all");
  const [settlementFilter, setSettlementFilter] = useState("all");
  const [adminFilter,      setAdminFilter]      = useState("all");
  const [filterData,       setFilterData]       = useState({ centers: [], adminUsers: [] });

  // ── fetch filter dropdown data ─────────────────────────────────────────────
  useEffect(() => {
    axiosInstance
      .get(`${API_BASE}/api/reports/filter-data`, { withCredentials: true })
      .then((r) => setFilterData(r.data))
      .catch(() => {});
  }, []);

  // ── fetch report ───────────────────────────────────────────────────────────
  const fetchReport = useCallback(async () => {
    try {
      setLoading(true); setError(null); setData(null);
      const params = { fromDate, toDate };
      if (activeTab === "partner-settlement") {
        params.centerId         = partnerFilter;
        params.settlementStatus = settlementFilter;
      }
      if (activeTab === "walkin") params.createdById = adminFilter;

      const res = await axiosInstance.get(`${API_BASE}/api/reports/${activeTab}`, {
        withCredentials: true,
        params,
      });
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load report");
    } finally {
      setLoading(false);
    }
  }, [activeTab, fromDate, toDate, partnerFilter, settlementFilter, adminFilter]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  // ── CSV export ─────────────────────────────────────────────────────────────
  const handleExport = async () => {
    try {
      setExporting(true);
      const params = new URLSearchParams({ type: activeTab, fromDate, toDate });
      const url    = `${API_BASE}/api/reports/export?${params}`;
      const res    = await axiosInstance.get(url, { withCredentials: true, responseType: "blob" });
      const blob   = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
      const link   = document.createElement("a");
      link.href     = URL.createObjectURL(blob);
      link.download = `${activeTab}-report-${fromDate}-to-${toDate}.csv`;
      link.click();
    } catch {
      alert("Export failed. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  // ── quick date presets ─────────────────────────────────────────────────────
  const setPreset = (preset) => {
    const now = new Date();
    if (preset === "today") {
      const d = today(); setFromDate(d); setToDate(d);
    } else if (preset === "week") {
      const s = new Date(now); s.setDate(now.getDate() - 6);
      setFromDate(s.toISOString().slice(0, 10)); setToDate(today());
    } else if (preset === "month") {
      setFromDate(monthStart()); setToDate(today());
    } else if (preset === "last-month") {
      const s = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const e = new Date(now.getFullYear(), now.getMonth(), 0);
      setFromDate(s.toISOString().slice(0, 10)); setToDate(e.toISOString().slice(0, 10));
    }
  };

  const inputCls =
    "px-3 py-2 border border-gray-300 rounded-lg text-xs bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500";

  return (
    <div className="min-h-screen bg-gray-50">
      <Header onMenuToggle={() => setSidebarOpen((s) => !s)} isMenuOpen={sidebarOpen} />
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        isCollapsed={sidebarCollapsed}
        onCollapseToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      <main className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"}`}>
        <div className="p-4 lg:p-6">

          {/* ── Page Header ───────────────────────────────────────────────── */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Icon name="BarChart3" size={18} className="text-blue-600" />
                </div>
                <h1 className="text-xl lg:text-2xl font-bold text-gray-900">Accounts & Reports</h1>
              </div>
              <p className="text-sm text-gray-500 mt-1 ml-10">
                Financial reports, collections, revenue & partner settlements
              </p>
            </div>

            <button
              onClick={handleExport}
              disabled={exporting || loading || !data}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {exporting
                ? <div className="w-3.5 h-3.5 border border-white border-t-transparent rounded-full animate-spin" />
                : <Icon name="Download" size={14} />}
              Export CSV
            </button>
          </div>

          {/* ── Filters bar ───────────────────────────────────────────────── */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm mb-5">
            <div className="flex flex-wrap items-end gap-3">

              {/* Date presets */}
              <div>
                <p className="text-[10px] font-medium text-gray-500 mb-1.5">Quick Select</p>
                <div className="flex gap-1">
                  {[["today","Today"],["week","7 Days"],["month","This Month"],["last-month","Last Month"]].map(([k,l]) => (
                    <button
                      key={k}
                      onClick={() => setPreset(k)}
                      className="px-2.5 py-1.5 text-[10px] font-medium rounded border border-gray-300 text-gray-600 hover:bg-gray-50 hover:border-gray-400 transition-colors"
                    >
                      {l}
                    </button>
                  ))}
                </div>
              </div>

              <div className="w-px h-8 bg-gray-200 hidden sm:block" />

              <div>
                <p className="text-[10px] font-medium text-gray-500 mb-1.5">From</p>
                <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className={inputCls} />
              </div>

              <div>
                <p className="text-[10px] font-medium text-gray-500 mb-1.5">To</p>
                <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className={inputCls} />
              </div>

              {/* Partner filter */}
              {activeTab === "partner-settlement" && (
                <>
                  <div>
                    <p className="text-[10px] font-medium text-gray-500 mb-1.5">Partner Center</p>
                    <select value={partnerFilter} onChange={(e) => setPartnerFilter(e.target.value)} className={inputCls}>
                      <option value="all">All Partners</option>
                      {filterData.centers.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <p className="text-[10px] font-medium text-gray-500 mb-1.5">Settlement</p>
                    <select value={settlementFilter} onChange={(e) => setSettlementFilter(e.target.value)} className={inputCls}>
                      <option value="all">All</option>
                      <option value="Paid">Paid</option>
                      <option value="Pending">Pending</option>
                    </select>
                  </div>
                </>
              )}

              {/* Admin filter */}
              {activeTab === "walkin" && (
                <div>
                  <p className="text-[10px] font-medium text-gray-500 mb-1.5">Admin User</p>
                  <select value={adminFilter} onChange={(e) => setAdminFilter(e.target.value)} className={inputCls}>
                    <option value="all">All Users</option>
                    {filterData.adminUsers.map((u) => (
                      <option key={u.id} value={u.id}>{u.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* ── Tab Navigation ─────────────────────────────────────────────── */}
          <div className="flex gap-2 mb-5 overflow-x-auto pb-1">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-medium whitespace-nowrap transition-all ${
                  activeTab === tab.id
                    ? "bg-blue-600 text-white shadow-sm"
                    : "bg-white border border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50"
                }`}
              >
                <Icon name={tab.icon} size={14} />
                {tab.label}
              </button>
            ))}
          </div>

          {/* ── Report Content ──────────────────────────────────────────────── */}
          <div className="space-y-5">
            {loading && (
              <div className="bg-white rounded-xl border border-gray-200">
                <Loading />
              </div>
            )}

            {error && !loading && (
              <div className="bg-white rounded-xl border border-red-200 p-6 text-center">
                <Icon name="AlertCircle" size={24} className="text-red-400 mx-auto mb-2" />
                <p className="text-sm text-red-600">{error}</p>
                <button
                  onClick={fetchReport}
                  className="mt-3 px-4 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Retry
                </button>
              </div>
            )}

            {!loading && !error && data && (
              <>
                {/* ═══════════════════════════════════════════════════════════
                    1️⃣  COLLECTION
                ═══════════════════════════════════════════════════════════ */}
                {activeTab === "collection" && (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <StatCard icon="IndianRupee" label="Total Collection" value={fmt(data.summary?.totalCollection)}
                        sub={`${fmtNum(data.summary?.totalOrders)} paid orders`} color="blue" highlight />
                      <StatCard icon="Tag"         label="Total Discount"  value={fmt(data.summary?.totalDiscount)}   color="amber" />
                      <StatCard icon="Receipt"     label="Avg Order Value" value={fmt(data.summary?.avgOrderValue)}   color="green" />
                      <StatCard icon="Package"     label="Total Orders"    value={fmtNum(data.summary?.totalOrders)}  color="violet" />
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-white rounded-xl border border-gray-200 p-5">
                        <h3 className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
                          <Icon name="CreditCard" size={15} className="text-blue-500" /> Payment Mode-wise
                        </h3>
                        <SectionTable
                          columns={[
                            { key: "mode",   label: "Mode"   },
                            { key: "count",  label: "Orders", align: "right", render: (v) => fmtNum(v) },
                            { key: "amount", label: "Amount", align: "right", render: (v) => fmt(v) },
                          ]}
                          rows={data.modeWise || []}
                        />
                      </div>

                      <div className="bg-white rounded-xl border border-gray-200 p-5">
                        <h3 className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
                          <Icon name="Smartphone" size={15} className="text-blue-500" /> App vs Walk-In
                        </h3>
                        <SectionTable
                          columns={[
                            { key: "source", label: "Source" },
                            { key: "count",  label: "Orders", align: "right", render: (v) => fmtNum(v) },
                            { key: "amount", label: "Amount", align: "right", render: (v) => fmt(v) },
                          ]}
                          rows={data.sourceWise || []}
                        />
                        <div className="mt-4 grid grid-cols-2 gap-3">
                          {(data.sourceWise || []).map((s) => {
                            const total = (data.sourceWise || []).reduce((x, r) => x + r.amount, 0);
                            const pct   = total ? Math.round((s.amount / total) * 100) : 0;
                            return (
                              <div key={s.source} className="rounded-lg bg-gray-50 p-3">
                                <p className="text-[10px] text-gray-500">{s.source}</p>
                                <p className="text-sm font-bold text-gray-800 mt-0.5">{pct}%</p>
                                <div className="w-full h-1.5 bg-gray-200 rounded-full mt-1.5">
                                  <div className="h-1.5 bg-blue-500 rounded-full" style={{ width: `${pct}%` }} />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-xl border border-gray-200 p-5">
                      <h3 className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <Icon name="Building2" size={15} className="text-blue-500" /> Center-wise Collection
                      </h3>
                      <SectionTable
                        columns={[
                          { key: "name",   label: "Center"  },
                          { key: "count",  label: "Orders", align: "right", render: (v) => fmtNum(v) },
                          { key: "amount", label: "Amount", align: "right", render: (v) => fmt(v) },
                        ]}
                        rows={data.centerWise || []}
                      />
                    </div>

                    <div className="bg-white rounded-xl border border-gray-200 p-5">
                      <h3 className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <Icon name="CalendarDays" size={15} className="text-blue-500" /> Date-wise Collection
                      </h3>
                      <SectionTable
                        columns={[
                          { key: "date",     label: "Date"    },
                          { key: "count",    label: "Orders",  align: "right", render: (v) => fmtNum(v) },
                          { key: "amount",   label: "Amount",  align: "right", render: (v) => fmt(v) },
                          { key: "discount", label: "Discount",align: "right", render: (v) => fmt(v) },
                        ]}
                        rows={[...(data.dateWise || [])].reverse()}
                        emptyMsg="No collections for this period"
                      />
                    </div>
                  </>
                )}

                {/* ═══════════════════════════════════════════════════════════
                    2️⃣  REVENUE
                ═══════════════════════════════════════════════════════════ */}
                {activeTab === "revenue" && (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <StatCard icon="IndianRupee" label="Net Revenue"        value={fmt(data.summary?.netRevenue)}
                        highlight color="blue" sub={`${fmtNum(data.summary?.totalOrders)} orders`} />
                      <StatCard icon="TrendingUp"  label="Gross Revenue"      value={fmt(data.summary?.grossRevenue)}      color="green" />
                      <StatCard icon="Tag"         label="Total Discount"     value={fmt(data.summary?.totalDiscount)}     color="amber" />
                      <StatCard icon="AlertCircle" label="Pending Collection" value={fmt(data.summary?.pendingCollection)} color="red" />
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-white rounded-xl border border-gray-200 p-5">
                        <h3 className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
                          <Icon name="PieChart" size={15} className="text-blue-500" /> Revenue Breakdown
                        </h3>
                        <div className="space-y-3">
                          {[
                            { label: "Novus Own Revenue",  value: data.summary?.novusRevenue,   color: "bg-blue-500",   count: data.summary?.novusOrderCount   },
                            { label: "Partner Revenue",    value: data.summary?.partnerRevenue, color: "bg-violet-500", count: data.summary?.partnerOrderCount },
                            { label: "Coupon Discount",    value: data.summary?.couponDiscount, color: "bg-amber-400",  count: null },
                            { label: "Other Discount",     value: data.summary?.otherDiscount,  color: "bg-red-400",    count: null },
                          ].map((item) => {
                            const total = data.summary?.grossRevenue || 1;
                            const pct   = Math.round(((item.value || 0) / total) * 100);
                            return (
                              <div key={item.label}>
                                <div className="flex justify-between text-xs mb-1">
                                  <span className="text-gray-600">
                                    {item.label}
                                    {item.count != null ? ` (${fmtNum(item.count)} orders)` : ""}
                                  </span>
                                  <span className="font-semibold text-gray-800">{fmt(item.value)}</span>
                                </div>
                                <div className="w-full h-2 bg-gray-100 rounded-full">
                                  <div className={`h-2 rounded-full ${item.color}`} style={{ width: `${Math.min(pct, 100)}%` }} />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      <div className="bg-white rounded-xl border border-gray-200 p-5">
                        <h3 className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
                          <Icon name="Receipt" size={15} className="text-blue-500" /> Discount Summary
                        </h3>
                        <SectionTable
                          columns={[
                            { key: "type",   label: "Type"   },
                            { key: "amount", label: "Amount", align: "right", render: (v) => fmt(v) },
                          ]}
                          rows={[
                            { type: "Coupon Discount", amount: data.summary?.couponDiscount },
                            { type: "Manual Discount", amount: data.summary?.otherDiscount },
                            { type: "Total Discount",  amount: data.summary?.totalDiscount },
                          ]}
                        />
                      </div>
                    </div>

                    <div className="bg-white rounded-xl border border-gray-200 p-5">
                      <h3 className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <Icon name="TrendingUp" size={15} className="text-blue-500" /> Monthly Revenue Trend
                      </h3>
                      <SectionTable
                        columns={[
                          { key: "month",    label: "Month"            },
                          { key: "novus",    label: "Novus Revenue",   align: "right", render: (v) => fmt(v) },
                          { key: "partner",  label: "Partner Revenue", align: "right", render: (v) => fmt(v) },
                          { key: "discount", label: "Discount",        align: "right", render: (v) => fmt(v) },
                          { key: "total",    label: "Net Revenue",     align: "right", render: (v) => fmt(v) },
                        ]}
                        rows={[...(data.monthlyTrend || [])].reverse()}
                        emptyMsg="No data for selected period"
                      />
                    </div>
                  </>
                )}

                {/* ═══════════════════════════════════════════════════════════
                    3️⃣  PARTNER SETTLEMENT  — per-test commission breakdown
                ═══════════════════════════════════════════════════════════ */}
                {activeTab === "partner-settlement" && (
                  <PartnerSettlementView data={data} fmt={fmt} fmtNum={fmtNum} />
                )}

                {/* ═══════════════════════════════════════════════════════════
                    4️⃣  PAYMENT GATEWAY
                ═══════════════════════════════════════════════════════════ */}
                {activeTab === "gateway" && (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                      <StatCard icon="CreditCard"  label="Total Transactions" value={fmtNum(data.summary?.totalTransactions)} highlight />
                      <StatCard icon="IndianRupee" label="Total Amount"       value={fmt(data.summary?.totalAmount)}          color="blue" />
                      <StatCard icon="CheckCircle" label="Captured"           value={fmt(data.summary?.capturedAmount)}       color="green" />
                      <StatCard icon="RefreshCw"   label="Refunded"           value={fmt(data.summary?.refundedAmount)}       color="violet" />
                      <StatCard icon="XCircle"     label="Failed Txns"        value={fmtNum(data.summary?.failedCount)}       color="red" />
                      <StatCard icon="Clock"       label="Pending Txns"       value={fmtNum(data.summary?.pendingCount)}      color="amber" />
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-white rounded-xl border border-gray-200 p-5">
                        <h3 className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
                          <Icon name="Globe" size={15} className="text-blue-500" /> Gateway-wise
                        </h3>
                        <SectionTable
                          columns={[
                            { key: "gateway",  label: "Gateway"   },
                            { key: "count",    label: "Txns",     align: "right", render: (v) => fmtNum(v) },
                            { key: "captured", label: "Captured", align: "right", render: (v) => fmt(v) },
                            { key: "refunded", label: "Refunded", align: "right", render: (v) => fmt(v) },
                            { key: "failed",   label: "Failed",   align: "right",
                              render: (v) => <Badge label={v} color={v > 0 ? "red" : "gray"} /> },
                          ]}
                          rows={data.gatewayWise || []}
                        />
                      </div>

                      <div className="bg-white rounded-xl border border-gray-200 p-5">
                        <h3 className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
                          <Icon name="Wallet" size={15} className="text-blue-500" /> Method-wise
                        </h3>
                        <SectionTable
                          columns={[
                            { key: "method", label: "Method"  },
                            { key: "count",  label: "Txns",   align: "right", render: (v) => fmtNum(v) },
                            { key: "amount", label: "Amount", align: "right", render: (v) => fmt(v) },
                          ]}
                          rows={data.methodWise || []}
                        />
                      </div>
                    </div>

                    {/* Failed transactions */}
                    {(data.failedTransactions?.length > 0) && (
                      <div className="bg-white rounded-xl border border-red-200 p-5">
                        <h3 className="text-sm font-semibold text-red-700 mb-4 flex items-center gap-2">
                          <Icon name="XCircle" size={15} className="text-red-500" />
                          Failed Transactions ({data.failedTransactions.length})
                        </h3>
                        <SectionTable
                          columns={[
                            { key: "paymentId",    label: "Txn ID" },
                            { key: "createdAt",    label: "Date",   render: (v) => new Date(v).toLocaleDateString("en-IN") },
                            { key: "paymentMethod",label: "Method" },
                            { key: "amount",       label: "Amount", align: "right", render: (v) => fmt(v) },
                            { key: "referenceId",  label: "Ref ID" },
                          ]}
                          rows={data.failedTransactions || []}
                        />
                      </div>
                    )}

                    {/* All transactions */}
                    <div className="bg-white rounded-xl border border-gray-200 p-5">
                      <h3 className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <Icon name="List" size={15} className="text-blue-500" /> All Transactions
                      </h3>
                      <SectionTable
                        columns={[
                          { key: "paymentId",      label: "Txn ID"   },
                          { key: "createdAt",      label: "Date",     render: (v) => new Date(v).toLocaleDateString("en-IN") },
                          { key: "paymentMethod",  label: "Method"   },
                          { key: "paymentStatus",  label: "Status",
                            render: (v) => (
                              <Badge
                                label={v}
                                color={
                                  ["CAPTURED","COMPLETED"].includes(v) ? "green"
                                  : v === "FAILED"   ? "red"
                                  : v === "REFUNDED" ? "violet"
                                  : "amber"
                                }
                              />
                            )
                          },
                          { key: "amount",         label: "Amount",  align: "right", render: (v) => fmt(v) },
                          { key: "capturedAmount", label: "Captured",align: "right", render: (v) => fmt(v) },
                          { key: "referenceId",    label: "Ref ID"   },
                        ]}
                        rows={(data.allTransactions || []).slice(0, 100)}
                        emptyMsg="No transactions for this period"
                      />
                      {(data.allTransactions?.length || 0) > 100 && (
                        <p className="text-[10px] text-gray-400 mt-3 text-center">
                          Showing first 100 rows. Export CSV for full data.
                        </p>
                      )}
                    </div>
                  </>
                )}

                {/* ═══════════════════════════════════════════════════════════
                    5️⃣  WALK-IN BILLING
                ═══════════════════════════════════════════════════════════ */}
                {activeTab === "walkin" && (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <StatCard icon="Store"       label="Walk-In Orders"  value={fmtNum(data.summary?.totalOrders)}  highlight />
                      <StatCard icon="IndianRupee" label="Total Billing"   value={fmt(data.summary?.totalAmount)}     color="blue" />
                      <StatCard icon="Banknote"    label="Cash Collection" value={fmt(data.summary?.cashAmount)}      color="green"
                        sub={`${fmtNum(data.summary?.cashCount)} orders`} />
                      <StatCard icon="CreditCard"  label="Digital"         value={fmt(data.summary?.digitalAmount)}   color="violet"
                        sub={`${fmtNum(data.summary?.digitalCount)} orders`} />
                    </div>

                    {/* Cash vs Digital bar */}
                    <div className="bg-white rounded-xl border border-gray-200 p-5">
                      <h3 className="text-sm font-semibold text-gray-800 mb-4">Cash vs Digital Split</h3>
                      <div className="space-y-3">
                        {[
                          { label: "Cash",    value: data.summary?.cashAmount,    color: "bg-emerald-500", count: data.summary?.cashCount },
                          { label: "Digital", value: data.summary?.digitalAmount, color: "bg-blue-500",    count: data.summary?.digitalCount },
                        ].map((item) => {
                          const total = data.summary?.totalAmount || 1;
                          const pct   = Math.round(((item.value || 0) / total) * 100);
                          return (
                            <div key={item.label}>
                              <div className="flex justify-between text-xs mb-1.5">
                                <span className="text-gray-600">
                                  {item.label}{" "}
                                  <span className="text-gray-400">({fmtNum(item.count)} orders)</span>
                                </span>
                                <span className="font-semibold text-gray-800">
                                  {fmt(item.value)}{" "}
                                  <span className="text-gray-400 font-normal">({pct}%)</span>
                                </span>
                              </div>
                              <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
                                <div
                                  className={`h-3 rounded-full ${item.color} transition-all duration-500`}
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-white rounded-xl border border-gray-200 p-5">
                        <h3 className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
                          <Icon name="Users" size={15} className="text-blue-500" /> Admin-wise Billing
                        </h3>
                        <SectionTable
                          columns={[
                            { key: "name",   label: "Admin User" },
                            { key: "count",  label: "Orders",    align: "right", render: (v) => fmtNum(v) },
                            { key: "amount", label: "Amount",    align: "right", render: (v) => fmt(v) },
                          ]}
                          rows={data.byUser || []}
                        />
                      </div>

                      <div className="bg-white rounded-xl border border-gray-200 p-5">
                        <h3 className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
                          <Icon name="CalendarCheck" size={15} className="text-blue-500" /> Daily Closing Report
                        </h3>
                        <SectionTable
                          columns={[
                            { key: "date",    label: "Date"    },
                            { key: "count",   label: "Orders", align: "right", render: (v) => fmtNum(v) },
                            { key: "cash",    label: "Cash",   align: "right", render: (v) => fmt(v) },
                            { key: "digital", label: "Digital",align: "right", render: (v) => fmt(v) },
                            { key: "total",   label: "Total",  align: "right", render: (v) => fmt(v) },
                          ]}
                          rows={data.dailyClosing || []}
                          emptyMsg="No walk-in data for this period"
                        />
                      </div>
                    </div>

                    {/* Detailed bill list */}
                    <div className="bg-white rounded-xl border border-gray-200 p-5">
                      <h3 className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <Icon name="ClipboardList" size={15} className="text-blue-500" /> Walk-In Bill Summary
                      </h3>
                      <SectionTable
                        columns={[
                          { key: "id",              label: "Order",   render: (v) => `ORD-${v}` },
                          { key: "createdAt",       label: "Date",    render: (v) => new Date(v).toLocaleDateString("en-IN") },
                          { key: "createdBy",       label: "Admin",   render: (v) => v?.name || "—" },
                          { key: "patient",         label: "Patient", render: (v) => v?.fullName || "—" },
                          { key: "diagnosticCenter",label: "Center",  render: (v) => v?.name || "—" },
                          { key: "paymentMode",     label: "Mode"     },
                          { key: "finalAmount",     label: "Amount",  align: "right", render: (v) => fmt(v) },
                          { key: "discount",        label: "Discount",align: "right", render: (v) => fmt(v) },
                          { key: "paymentStatus",   label: "Payment",
                            render: (v) => (
                              <Badge
                                label={v === "pending" ? "DUE" : v}
                                color={v === "pending" ? "amber" : "green"}
                              />
                            )
                          },
                        ]}
                        rows={(data.rows || []).slice(0, 100)}
                        emptyMsg="No walk-in orders for this period"
                      />
                      {(data.rows?.length || 0) > 100 && (
                        <p className="text-[10px] text-gray-400 mt-3 text-center">
                          Showing first 100 rows. Export CSV for full data.
                        </p>
                      )}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}